/**
 * AX-Agent 임베드(iframe) ↔ 호스트 postMessage 브리지.
 *
 * ax-agent `packages/embed-sdk` 의 메시지 계약을 호스트 측에서 구현한다
 * (docs/designs/mes-agent-addon-integration.md §3.2·§3.3 + 계약 v2:
 * docs/designs/mes-agent-screen-interaction.md §5.1~§5.2).
 *
 * - 패널 `ax:ready` 수신 → embed 토큰 발급(`POST /api/auth/agent-embed-token`) 후
 *   `ax:init { auth_token, tenant, capabilities }` 전송 + 카탈로그·현재 화면 컨텍스트 전송.
 * - 토큰 만료의 80% 시점에 선제 재발급, 패널이 401 을 만나 `ax:token_expired` 를
 *   보내와도 즉시 재발급해 재전송한다.
 * - 라우트 변경·활성 그리드 변경 시 `ax:context { screen, screen_name, path, grid }` 갱신.
 * - v2 수신: `ax:navigate` 는 **권한 재검사 후** 이동(패널·에이전트 BE 는 신뢰 경계 밖),
 *   `ax:grid:request` 는 그리드 레지스트리 스냅샷으로 `ax:grid:data` 응답.
 */

import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue';
import { useRoute, useRouter, type LocationQueryRaw, type RouteLocationRaw } from 'vue-router';

import { getApiClient } from '../api/client';
import { usePermission } from '../composables/usePermission';
import { useTenantNav } from '../composables/useTenantNav';
import { useAuthStore } from '../stores/auth';
import { useMenuStore } from '../stores/menu';
import { buildAgentCatalog, type AgentScreenEntry } from './agent-catalog';
import {
  agentGridContextMeta,
  agentGridVersion,
  snapshotAgentGrid,
  type AgentGridColumn,
  type AgentGridMeta,
} from './grid-registry';
import { AGENT_EMBED_URL, AGENT_TENANT_OVERRIDE } from './agent-addon.config';

type HostToPanelMessage =
  | {
      type: 'ax:init';
      auth_token?: string;
      tenant?: string;
      locale?: string;
      capabilities?: string[];
    }
  | { type: 'ax:catalog'; screens: AgentScreenEntry[] }
  | {
      type: 'ax:context';
      screen: string;
      selection?: Record<string, string>;
      screen_name?: string;
      path?: string;
      grid?: AgentGridMeta;
    }
  | {
      type: 'ax:grid:data';
      request_id: string;
      grid_id: string;
      columns: AgentGridColumn[];
      rows: Record<string, unknown>[];
      total_rows: number;
      truncated: boolean;
    };

interface PanelNavigateMessage {
  target?: unknown;
  params?: Record<string, string>;
  query?: Record<string, string>;
}

interface PanelGridRequestMessage {
  request_id?: unknown;
  grid_id?: unknown;
  max_rows?: unknown;
}

/** 계약 v2 기능 협상 — 패널은 이 목록이 없으면(v1 호스트) 해당 기능을 비노출한다. */
const AGENT_CAPABILITIES = ['navigate', 'catalog', 'grid'];

function resolveEmbedOrigin(): string {
  try {
    return new URL(AGENT_EMBED_URL, window.location.href).origin;
  } catch {
    return window.location.origin;
  }
}

export function useAgentEmbedBridge(iframeRef: Ref<HTMLIFrameElement | null>): void {
  const embedOrigin = resolveEmbedOrigin();
  const route = useRoute();
  const router = useRouter();
  const permissions = usePermission();
  const tenantNav = useTenantNav();
  const menuStore = useMenuStore();
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let contextTimer: ReturnType<typeof setTimeout> | null = null;

  function post(message: HostToPanelMessage): void {
    iframeRef.value?.contentWindow?.postMessage(message, embedOrigin);
  }

  function resolveTenant(): string {
    if (AGENT_TENANT_OVERRIDE) return AGENT_TENANT_OVERRIDE;
    try {
      return useAuthStore().user?.tenantCode || 'default';
    } catch {
      return 'default';
    }
  }

  function scheduleRefresh(expiresInSec: number): void {
    if (refreshTimer) clearTimeout(refreshTimer);
    const delayMs = Math.max(30_000, expiresInSec * 1000 * 0.8);
    refreshTimer = setTimeout(() => void sendInit(), delayMs);
  }

  async function sendInit(): Promise<void> {
    try {
      const res = await getApiClient().post<{ token: string; expiresIn: number }>(
        '/auth/agent-embed-token',
      );
      post({
        type: 'ax:init',
        auth_token: res.data.token,
        tenant: resolveTenant(),
        locale: 'ko-KR',
        capabilities: AGENT_CAPABILITIES,
      });
      scheduleRefresh(res.data.expiresIn);
    } catch {
      // 미로그인/발급 실패 — 토큰 없이 테넌트만 전달(개발 모드 임베드는 무토큰 동작).
      post({
        type: 'ax:init',
        tenant: resolveTenant(),
        locale: 'ko-KR',
        capabilities: AGENT_CAPABILITIES,
      });
    }
  }

  // ------------------------------------------------------------ 화면 카탈로그

  function sendCatalog(): void {
    // 메뉴 트리(권한·경로·제목의 SSOT)에서 생성 — 사용자 무권한 화면은 노출하지 않는다.
    const screens = buildAgentCatalog(menuStore.tree, permissions.hasPermission);
    if (screens.length) post({ type: 'ax:catalog', screens });
  }

  // ------------------------------------------------------------ 화면 컨텍스트

  function currentScreenLabel(): string {
    const metaTitle = route.meta?.title;
    if (typeof metaTitle === 'string' && metaTitle) return metaTitle;
    return document.title || String(route.path);
  }

  function sendContext(): void {
    const grid = agentGridContextMeta();
    post({
      type: 'ax:context',
      screen: currentScreenLabel(),
      screen_name: typeof route.name === 'string' ? route.name : route.path,
      path: route.path,
      ...(grid ? { grid } : {}),
    });
  }

  /** 라우트 이동 직후 그리드 unmount→mount 가 연달아 발생 — 마지막 상태만 전송. */
  function scheduleContext(): void {
    if (contextTimer) clearTimeout(contextTimer);
    contextTimer = setTimeout(() => sendContext(), 150);
  }

  // ------------------------------------------------------- v2 수신 핸들러

  function handleNavigate(message: PanelNavigateMessage): void {
    const target = typeof message.target === 'string' ? message.target : '';
    if (!target || target === '#') return;

    let location: RouteLocationRaw | null = null;
    let permission: unknown;
    const query = (message.query ?? undefined) as LocationQueryRaw | undefined;
    if (target.startsWith('/')) {
      // 카탈로그 식별자 = app path — 테넌트 prefix 부여 후 재해석
      const fullPath = tenantNav.to(target);
      const resolved = router.resolve(fullPath);
      if (resolved.name === undefined || resolved.name === 'not-found') {
        console.warn('[agent-addon] navigate 대상 라우트 없음:', target);
        return;
      }
      permission = resolved.meta?.permission;
      location = { path: fullPath, ...(query !== undefined ? { query } : {}) };
    } else if (router.hasRoute(target)) {
      try {
        const params = { ...route.params, ...(message.params ?? {}) };
        permission = router.resolve({ name: target, params }).meta?.permission;
        location = { name: target, params, ...(query !== undefined ? { query } : {}) };
      } catch {
        return;
      }
    } else {
      console.warn('[agent-addon] navigate 대상 없음:', target);
      return;
    }

    // 패널이 보낸 대상을 신뢰하지 않는다 — 카탈로그가 권한 필터되어 있어도 재검사 (§5.6)
    if (typeof permission === 'string' && permission && !permissions.hasPermission(permission)) {
      console.warn('[agent-addon] navigate 권한 없음:', target);
      return;
    }
    if (Array.isArray(permission) && !permissions.hasAllPermissions(...(permission as string[]))) {
      console.warn('[agent-addon] navigate 권한 없음:', target);
      return;
    }
    if (!location) return;
    void router.push(location);
  }

  function handleGridRequest(message: PanelGridRequestMessage): void {
    const requestId = typeof message.request_id === 'string' ? message.request_id : '';
    if (!requestId) return;
    const snapshot = snapshotAgentGrid(
      typeof message.grid_id === 'string' && message.grid_id ? message.grid_id : undefined,
      typeof message.max_rows === 'number' ? message.max_rows : undefined,
    );
    // 그리드가 없어도 응답한다 — 패널 promise 가 타임아웃 대신 빈 데이터로 정리되게.
    post({
      type: 'ax:grid:data',
      request_id: requestId,
      grid_id: snapshot?.grid_id ?? '',
      columns: snapshot?.columns ?? [],
      rows: snapshot?.rows ?? [],
      total_rows: snapshot?.total_rows ?? 0,
      truncated: snapshot?.truncated ?? false,
    });
  }

  function onWindowMessage(event: MessageEvent): void {
    if (event.origin !== embedOrigin) return;
    const data = event.data as { type?: string } | null;
    const type = data?.type;
    if (type === 'ax:ready') {
      void sendInit().then(() => {
        sendCatalog();
        sendContext();
      });
    } else if (type === 'ax:token_expired') {
      void sendInit();
    } else if (type === 'ax:navigate') {
      handleNavigate(event.data as PanelNavigateMessage);
    } else if (type === 'ax:grid:request') {
      handleGridRequest(event.data as PanelGridRequestMessage);
    }
  }

  onMounted(() => {
    window.addEventListener('message', onWindowMessage);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('message', onWindowMessage);
    if (refreshTimer) clearTimeout(refreshTimer);
    if (contextTimer) clearTimeout(contextTimer);
  });

  // 패널이 이미 떠 있는 상태의 라우트 이동 — 화면 컨텍스트 갱신.
  watch(
    () => route.fullPath,
    () => scheduleContext(),
  );

  // 활성 그리드 등록/해제/전환 — 그리드 메타 갱신.
  watch(agentGridVersion, () => scheduleContext());

  // 메뉴 트리는 비동기 로드 — ax:ready 시점에 비어 있었으면 로드 후 재전송.
  watch(
    () => menuStore.tree,
    () => sendCatalog(),
  );

  // iframe 이 (재)로드되면 load 이벤트 기준으로도 init 을 밀어 넣는다 —
  // ax:ready 가 리스너 부착 전에 발화하는 레이스의 안전망.
  watch(iframeRef, (el, _prev, onCleanup) => {
    if (!el) return;
    const onLoad = () =>
      void sendInit().then(() => {
        sendCatalog();
        sendContext();
      });
    el.addEventListener('load', onLoad);
    onCleanup(() => el.removeEventListener('load', onLoad));
  });
}
